// Hub éditorial /fr/guides — page pilier de la section
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { JsonLd, SeoPage, useHreflang, useSeo } from "@/components/seo/Seo";
import { ClusterCard, GuideCard } from "@/components/guides/GuideBlocks";
import { BASE_URL, LANGS, p } from "@/lib/seoContent";
import { CLUSTERS, GUIDES_COPY, aboutUrl, guidesHubUrl, hubIndexable, publishedGuides } from "@/lib/guides";

export default function GuidesIndex({ lang }) {
  const c = GUIDES_COPY[lang];
  const path = guidesHubUrl(lang);
  const alternates = Object.fromEntries(LANGS.map((l) => [l, guidesHubUrl(l)]));
  const guides = publishedGuides(lang);
  const noindex = !hubIndexable(lang);
  useSeo({ lang, title: c.seo_title, description: c.meta, path, type: "guides", noindex });
  useHreflang(alternates);
  const crumbs = [{ label: c.home, to: p(lang) }, { label: c.title }];
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.label, ...(x.to ? { item: `${BASE_URL}${x.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "CollectionPage", name: c.title, url: `${BASE_URL}${path}`, inLanguage: lang,
      publisher: { "@type": "Organization", name: "MILES", url: BASE_URL } },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId="guides-index">
      <JsonLd id="guides-index" data={jsonLd} />
      <header className="max-w-3xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{c.eyebrow}</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-ink sm:text-5xl">{c.h1}</h1>
        <p className="mt-5 text-base leading-relaxed text-slate sm:text-lg">{c.intro}</p>
        <Link to={aboutUrl(lang)} data-testid="guides-about-link" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-miles hover:underline">
          {c.about_link} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
        </Link>
      </header>

      {guides.length > 0 && (
        <section className="mt-14" data-testid="guides-latest">
          <h2 className="text-lg font-bold text-ink sm:text-xl">{c.latest}</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {guides.slice(0, 6).map((g, i) => <GuideCard key={g.id} g={g} lang={lang} large={i === 0 && false} />)}
          </div>
        </section>
      )}

      <section className="mt-16" data-testid="guides-clusters">
        <h2 className="text-lg font-bold text-ink sm:text-xl">{c.universes}</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Object.keys(CLUSTERS).map((id) => <ClusterCard key={id} id={id} lang={lang} />)}
        </div>
      </section>
    </SeoPage>
  );
}
